// src/components/traffic/MonthlyTraffic.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Grid, Card, CardContent, CardHeader, Typography, Button,
    TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
    Paper, Chip, LinearProgress, Collapse, IconButton, CircularProgress
} from '@mui/material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    Legend, ResponsiveContainer
} from 'recharts';
import { 
    Refresh as RefreshIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import { get } from '../../services/apiService';

const translateMonthName = (monthStr) => {
    if (!monthStr) return '';
    const cleanMonthStr = monthStr.replace(/\s+/g, ' ').trim();
    const parts = cleanMonthStr.split(' ');
    if (parts.length !== 2) return monthStr;
    
    const [monthName, year] = parts;
    const translations = {
        january: 'Enero',
        february: 'Febrero',
        march: 'Marzo',
        april: 'Abril',
        may: 'Mayo',
        june: 'Junio',
        july: 'Julio',
        august: 'Agosto',
        september: 'Septiembre',
        october: 'Octubre',
        november: 'Noviembre',
        december: 'Diciembre'
    };
    
    const translatedName = translations[monthName.toLowerCase()] || monthName;
    return `${translatedName} ${year}`;
};

function MonthlyTrafficRow({ row, clientId }) {
    const [open, setOpen] = useState(false);
    const [dailyData, setDailyData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Parsear el mes y año de row.month (ej: "July 2026")
    const getMonthAndYear = (monthStr) => {
        const cleanMonthStr = monthStr.replace(/\s+/g, ' ').trim();
        const [monthName, yearStr] = cleanMonthStr.split(' ');
        const year = parseInt(yearStr);
        
        const monthMap = {
            january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
            july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
            enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6,
            julio: 7, agosto: 8, septiembre: 9, october: 10, noviembre: 11, diciembre: 12
        };
        
        const month = monthMap[monthName.toLowerCase()] || 1;
        return { month, year };
    };

    const fetchDailyData = async () => {
        if (!clientId) return;
        try {
            setLoading(true);
            setError(null);
            const { month, year } = getMonthAndYear(row.month);
            const result = await get(`/api/traffic/daily-by-month/${clientId}?year=${year}&month=${month}`);
            setDailyData(result || []);
        } catch (err) {
            console.error('Error al obtener tráfico diario del mes:', err);
            setError('Error al cargar detalle diario.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = () => {
        const nextState = !open;
        setOpen(nextState);
        if (nextState && dailyData.length === 0) {
            fetchDailyData();
        }
    };

    const formatDateInSpanish = (dateStr) => {
        const parts = dateStr.split('-');
        if (parts.length !== 3) return dateStr;
        const day = parseInt(parts[2]);
        const month = parseInt(parts[1]) - 1;
        const year = parseInt(parts[0]);
        const date = new Date(year, month, day);
        return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
    };

    const downloadGB = parseFloat(row.download || 0);
    const uploadGB = parseFloat(row.upload || 0);
    const totalGB = downloadGB + uploadGB;

    return (
        <>
            <TableRow 
                hover 
                onClick={handleRowClick}
                sx={{ 
                    cursor: 'pointer',
                    '& > *': { borderBottom: 'unset' },
                    backgroundColor: open ? 'rgba(63, 81, 181, 0.03)' : 'inherit'
                }}
            >
                <TableCell width="40px" style={{ padding: '6px 16px' }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRowClick(); }}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row" sx={{ fontWeight: '500' }}>
                    {translateMonthName(row.month)}
                </TableCell>
                {!clientId && (
                    <TableCell>{row.clientName || ''}</TableCell>
                )}
                <TableCell align="right">
                    <Chip
                        size="small"
                        label={`${Math.round(downloadGB * 100) / 100} GB`}
                        sx={{ backgroundColor: 'rgba(63, 81, 181, 0.1)', fontWeight: 500 }}
                    />
                </TableCell>
                <TableCell align="right">
                    <Chip
                        size="small"
                        label={`${Math.round(uploadGB * 100) / 100} GB`}
                        sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)', fontWeight: 500 }}
                    />
                </TableCell>
                <TableCell align="right">
                    <Chip
                        size="small"
                        label={`${Math.round(totalGB * 100) / 100} GB`}
                        sx={{ fontWeight: 500 }}
                    />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={!clientId ? 6 : 5}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ 
                            margin: 1.5, 
                            pl: 3, 
                            borderLeft: '3px solid #3f51b5',
                            pb: 1.5
                        }}>
                            <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: '600', color: '#3f51b5', mb: 1 }}>
                                Detalle de Consumo Diario: {translateMonthName(row.month)}
                            </Typography>
                            
                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">
                                        Cargando consumo diario...
                                    </Typography>
                                </Box>
                            ) : error ? (
                                <Typography variant="body2" color="error" sx={{ py: 1 }}>
                                    {error}
                                </Typography>
                            ) : dailyData.length > 0 ? (
                                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(224, 224, 224, 1)', maxHeight: 300, overflowY: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ backgroundColor: 'rgb(246, 248, 250)', fontWeight: 'bold' }}>Día</TableCell>
                                                <TableCell align="right" sx={{ backgroundColor: 'rgb(246, 248, 250)', fontWeight: 'bold' }}>Download</TableCell>
                                                <TableCell align="right" sx={{ backgroundColor: 'rgb(246, 248, 250)', fontWeight: 'bold' }}>Upload</TableCell>
                                                <TableCell align="right" sx={{ backgroundColor: 'rgb(246, 248, 250)', fontWeight: 'bold' }}>Total</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dailyData.map((dayRow) => {
                                                const dayDownload = parseFloat(dayRow.download || 0);
                                                const dayUpload = parseFloat(dayRow.upload || 0);
                                                const dayTotal = dayDownload + dayUpload;
                                                return (
                                                    <TableRow key={dayRow.date} hover>
                                                        <TableCell>{formatDateInSpanish(dayRow.date)}</TableCell>
                                                        <TableCell align="right">{Math.round(dayDownload * 100) / 100} GB</TableCell>
                                                        <TableCell align="right">{Math.round(dayUpload * 100) / 100} GB</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: '500' }}>{Math.round(dayTotal * 100) / 100} GB</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                    No hay registros diarios disponibles para este mes.
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

function MonthlyTraffic({ clientId, months = 12, setError }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchMonthlyData = useCallback(async () => {
        try {
            setLoading(true);

            // Si no hay cliente seleccionado, no hacemos la petición
            if (!clientId) {
                setData([]);
                setLastUpdated(new Date());
                setError('Seleccione un cliente para ver su histórico anual de tráfico.');
                return;
            }

            const url = `/api/traffic/monthly/${clientId}?months=${months}`;

            const result = await get(url);
            setData(result);
            setLastUpdated(new Date());
        } catch (error) {
            console.error('Error al obtener datos mensuales:', error);
            setError('No se pudieron cargar los datos mensuales. Por favor, inténtelo de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [clientId, months, setError]);

    // Load data when component mounts
    useEffect(() => {
        fetchMonthlyData();
    }, [fetchMonthlyData]);

    // Format data for chart
    const prepareChartData = () => {
        // If no specific client is selected, group by month
        if (!clientId) {
            // Group by month and sum values
            const groupedByMonth = data.reduce((acc, item) => {
                const month = item.month;
                if (!acc[month]) {
                    acc[month] = {
                        month,
                        downloadGB: 0,
                        uploadGB: 0,
                        totalGB: 0
                    };
                }

                // Sum values in GB
                const downloadGB = parseFloat(item.download || 0);
                const uploadGB = parseFloat(item.upload || 0);

                acc[month].downloadGB += downloadGB;
                acc[month].uploadGB += uploadGB;
                acc[month].totalGB += downloadGB + uploadGB;

                return acc;
            }, {});

            return Object.values(groupedByMonth)
                .sort((a, b) => {
                    // Sort by date (assuming "Mmm YYYY" format)
                    const dateA = new Date(a.month);
                    const dateB = new Date(b.month);
                    return dateA - dateB;
                })
                .map(item => ({
                    ...item,
                    month: translateMonthName(item.month),
                    // Format to consistent 2 decimals using Math.round for clean values
                    downloadGB: Math.round(item.downloadGB * 100) / 100,
                    uploadGB: Math.round(item.uploadGB * 100) / 100,
                    totalGB: Math.round(item.totalGB * 100) / 100
                }));
        }

        // If there's a specific client, simply format the values - with fixed decimals
        return data
            .sort((a, b) => {
                // Sort by date (assuming "Mmm YYYY" format)
                const dateA = new Date(a.month);
                const dateB = new Date(b.month);
                return dateA - dateB;
            })
            .map(item => {
                const download = parseFloat(item.download || 0);
                const upload = parseFloat(item.upload || 0);
                return {
                    month: translateMonthName(item.month),
                    downloadGB: Math.round(download * 100) / 100,
                    uploadGB: Math.round(upload * 100) / 100,
                    totalGB: Math.round((download + upload) * 100) / 100
                };
            });
    };

    const chartData = prepareChartData();

    // Function to update data
    const handleRefresh = async () => {
        await fetchMonthlyData();
    };

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mb: 2
            }}>
                <Typography variant="h6" component="h2" fontWeight="500">
                    Histórico Anual
                    {lastUpdated && (
                        <Typography variant="body2" component="span" sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 0.5, sm: 0 }, color: 'text.secondary', display: { xs: 'block', sm: 'inline' } }}>
                            Datos cargados: {lastUpdated.toLocaleTimeString()}
                        </Typography>
                    )}
                </Typography>
                <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={loading || !clientId}
                    variant="outlined"
                    sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
                >
                    Actualizar
                </Button>
            </Box>

            {loading && <LinearProgress sx={{ mb: 2 }} />}

            {/* Mensaje cuando no hay cliente seleccionado */}
            {!clientId && (
                <Paper
                    sx={{
                        p: 3,
                        mb: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '200px',
                        backgroundColor: 'rgb(246, 248, 250)'
                    }}
                >
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No hay cliente seleccionado
                    </Typography>
                    <Typography variant="body1" color="text.secondary" align="center">
                        Por favor, seleccione un cliente en el campo superior para ver su histórico anual de tráfico.
                    </Typography>
                </Paper>
            )}

            <Grid container spacing={3}>
                {/* Monthly consumption chart */}
                <Grid item xs={12}>
                    <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.05)', minWidth: 0 }}>
                        <CardHeader
                            title="Consumo Mensual (GB)"
                            titleTypographyProps={{ fontWeight: '500' }}
                        />
                        <CardContent>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={chartData}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis
                                        dataKey="month"
                                        tick={{ fontSize: 12, fill: '#666' }}
                                        tickMargin={8}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 12, fill: '#666' }}
                                        tickMargin={8}
                                    />
                                    <RechartsTooltip
                                        formatter={(value) => [`${value} GB`, '']}
                                        contentStyle={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            border: 'none',
                                            borderRadius: '4px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}
                                        labelStyle={{ color: '#333', fontWeight: 'bold' }}
                                    />
                                    <Legend />
                                    <Bar
                                        dataKey="downloadGB"
                                        name="Download"
                                        fill="#3f51b5"
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Bar
                                        dataKey="uploadGB"
                                        name="Upload"
                                        fill="#4caf50"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Monthly consumption table */}
                <Grid item xs={12}>
                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': {
                                height: '6px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(0,0,0,0.15)',
                                borderRadius: '4px',
                            }
                        }}
                    >
                        <Table>
                            <TableHead sx={{ backgroundColor: 'rgb(246, 248, 250)' }}>
                                <TableRow>
                                    <TableCell width="40px" />
                                    <TableCell>Mes</TableCell>
                                    {!clientId && <TableCell>Cliente</TableCell>}
                                    <TableCell align="right">Download</TableCell>
                                    <TableCell align="right">Upload</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? (
                                    data.map((row, index) => (
                                        <MonthlyTrafficRow key={index} row={row} clientId={clientId} />
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={!clientId ? 6 : 5} align="center">
                                            No hay datos disponibles
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
}

export default MonthlyTraffic;